import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CurrencyDollar, ArrowsLeftRight, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function CurrencySettings() {
  var [currencies, setCurrencies] = useState({});
  var [preference, setPreference] = useState('USD');
  var [loading, setLoading] = useState(true);
  var [convertFrom, setConvertFrom] = useState('USD');
  var [convertTo, setConvertTo] = useState('EUR');
  var [convertAmount, setConvertAmount] = useState(100);
  var [convertResult, setConvertResult] = useState(null);

  useEffect(function () {
    Promise.all([api.get('/currency/supported'), api.get('/currency/preference')])
      .then(function (results) { setCurrencies(results[0].data); setPreference(results[1].data.currency); })
      .catch(console.error).finally(function () { setLoading(false); });
  }, []);

  var savePref = async function (curr) {
    try {
      await api.put('/currency/preference', { currency: curr });
      setPreference(curr);
      toast.success('Currency preference updated to ' + curr);
    } catch (err) { toast.error('Failed to update'); }
  };

  var convert = async function () {
    try {
      var resp = await api.get('/currency/convert?amount=' + convertAmount + '&from_curr=' + convertFrom + '&to_curr=' + convertTo);
      setConvertResult(resp.data);
    } catch (err) { toast.error('Conversion failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  var currList = Object.entries(currencies);

  return (
    <div className="space-y-6" data-testid="currency-settings">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <CurrencyDollar size={32} className="text-emerald-400" weight="duotone" />
          Multi-Currency Settings
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Configure your preferred currency for payouts and dashboards</p>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white">Your Preferred Currency</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {currList.map(function (entry) {
              var code = entry[0];
              var info = entry[1];
              var isActive = preference === code;
              return (
                <button key={code} data-testid={'currency-' + code} onClick={function () { savePref(code); }}
                  className={'p-3 rounded-lg border text-left transition-all ' +
                    (isActive ? 'bg-blue-600/15 border-blue-500/30 text-blue-400' : 'bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-700')}>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold font-heading">{info.symbol}</span>
                    {isActive && <Check size={14} className="text-blue-400" />}
                  </div>
                  <p className="text-xs font-medium mt-1">{code}</p>
                  <p className="text-[10px] text-zinc-500 truncate">{info.name}</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white flex items-center gap-2"><ArrowsLeftRight size={20} className="text-blue-400" /> Currency Converter</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="text-xs text-zinc-500 mb-1 block">Amount</label>
              <Input data-testid="convert-amount" type="number" value={convertAmount} onChange={function (e) { setConvertAmount(Number(e.target.value)); }} className="bg-zinc-950 border-zinc-800 text-zinc-100" />
            </div>
            <div className="w-28">
              <label className="text-xs text-zinc-500 mb-1 block">From</label>
              <select data-testid="convert-from" value={convertFrom} onChange={function (e) { setConvertFrom(e.target.value); }} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-100 text-sm">
                {currList.map(function (e) { return <option key={e[0]} value={e[0]}>{e[0]}</option>; })}
              </select>
            </div>
            <div className="w-28">
              <label className="text-xs text-zinc-500 mb-1 block">To</label>
              <select data-testid="convert-to" value={convertTo} onChange={function (e) { setConvertTo(e.target.value); }} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-100 text-sm">
                {currList.map(function (e) { return <option key={e[0]} value={e[0]}>{e[0]}</option>; })}
              </select>
            </div>
            <Button data-testid="convert-btn" onClick={convert} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
              <ArrowsLeftRight size={14} /> Convert
            </Button>
          </div>
          {convertResult && (
            <div className="mt-4 p-4 bg-zinc-950 rounded-lg border border-zinc-800" data-testid="convert-result">
              <p className="text-sm text-zinc-400">{currencies[convertResult.from]?.symbol}{convertResult.original.toLocaleString()} {convertResult.from}</p>
              <p className="text-2xl font-bold font-heading text-emerald-400 mt-1">{currencies[convertResult.to]?.symbol}{convertResult.converted.toLocaleString()} {convertResult.to}</p>
              <p className="text-xs text-zinc-500 mt-1">Rate: 1 {convertResult.from} = {convertResult.rate} {convertResult.to}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white">Exchange Rates (vs USD)</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {currList.map(function (entry) {
              var code = entry[0];
              var info = entry[1];
              return (
                <div key={code} className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-bold text-zinc-200">{code}</span>
                    <Badge className="bg-zinc-800 text-zinc-400 text-[10px]">{info.symbol}</Badge>
                  </div>
                  <p className="text-lg font-bold font-heading text-blue-400">{info.rate}</p>
                  <p className="text-[10px] text-zinc-500">{info.name}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
